import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { getThresholdsKeys } from './Thresholds';
import { LQMOS_RX, LQMOS_TX } from './columns/VoIPColumns';
import { getWifiSearchFields } from './columns/WiFiColumns';

export const TEST_TYPE_GROUPS = {
    WEB: 'Web',
    VOIP: 'VoIP',
};

export const BASE_METRICS = [
    'testTime',
    'status',
    'npoint-isp',
    'npoint-city',
    'npoint-subcode',
    'sem-capture-truncated',
    'sem-capture-error',
];
export const BASE_SEARCH_FIELDS = ['status', 'agent', 'npoint-isp'];
export const WIFI_METRICS = [
    'adapter_type',
    'wifi_connected',
    'wifi_connection_errors',
    'wifi_ssid',
    'wifi_connection_rate',
    'wifi_signal_level_pct',
    'wifi_signal_level_dbm',
    'wifi_channel',
    'wifi_channel_width',
    'wifi_frequency',
    'wifi_bssid',
    'wifi_ip_address',
    'wifi_technology',
    'deviceName',
    'device',
    'radioName',
    'radio',
];
export const WIFI_VITALS_METRICS = [
    'wifi_ssid',
    'wifi_bssid',
    'wifi_ip_address',
    'wifi_signal_level_dbm',
    'wifi_channel',
    'wifi_connection_errors',
];
export const WIFI_VITALS_SEARCH_FIELDS = ['wifi_ssid', 'wifi_bssid', 'wifi_ip_address', 'wifi_connection_errors'];
export const WEB_METRICS = [
    ...BASE_METRICS,
    'avgResponse',
    'avgDns',
    'avgNetwork',
    'avgSsl',
    'avgClient',
    'avgApp',
    'avgServer',
    'avgResponseCode',
    ...WIFI_METRICS,
];
export const WEB_PATH_METRICS = [
    ...BASE_METRICS,
    'avgResponse',
    'pathHops',
    'pathLatency',
    'pathDestIp',
    'pathDestDns',
];
export const WEB_SEARCH_FIELDS = [...BASE_SEARCH_FIELDS, 'avgResponseCode'];
export const VOIP_METRICS = [
    ...BASE_METRICS,
    'avgErrorCode',
    'isCaller',
    'avgSipStatus',
    'avgLqmosRx',
    'avgLqmosTx',
    'avgPctLossRx',
    'avgPctLossTx',
    'avgRttRx',
    'avgRttTx',
    'avgJitterRx',
    'avgJitterTx',
    'avgCallDuration',
    'avgDialDelay',
    'avgRingDelay',
    'avgCodecPt',
    'avgSecurityMode',
    ...WIFI_METRICS,
];
export const CUSTOM_METRICS = [
    ...BASE_METRICS,
    'message',
    'message_detail',
    'message_short',
    'failed_step',
    ...WIFI_METRICS,
];

export function getWebMetricNames(test) {
    const thresholdMetrics = getThresholdsKeys('Response', test);
    return WEB_METRICS.concat([thresholdMetrics.degraded, thresholdMetrics.critical]);
}

export function getWebPathMetricNames(test) {
    const thresholdMetrics = getThresholdsKeys('Response', test);
    return WEB_PATH_METRICS.concat([thresholdMetrics.degraded, thresholdMetrics.critical]);
}

export function getVoIPMetricNames(test) {
    const rxThresholds = getThresholdsKeys(LQMOS_RX, test);
    const txThresholds = getThresholdsKeys(LQMOS_TX, test);
    return VOIP_METRICS.concat([
        rxThresholds.degraded,
        rxThresholds.critical,
        txThresholds.degraded,
        txThresholds.critical,
    ]);
}

/**
 * The metric names are stored as min, max, avg, count, we need to prefix the metric to get the right value.
 */
function modifyMetricName(metric) {
    if (metric.isString === true) {
        return metric.name;
    }

    return `avg${metric.name}`;
}

export function getCustomMetrics(test) {
    const hideTheseMetrics = ['availability', 'message'];
    const metrics = _.chain(test?.typeNode?.metrics)
        .filter((metric) => _.indexOf(hideTheseMetrics, metric.name) < 0)
        .orderBy(['isString'], ['asc'])
        .value();

    // Until we give users a way to order the metrics as opposed to fifo, we'll at least push the string metrics to the end.
    return [
        ...metrics.filter((metric) => metric.isString === false),
        ...metrics.filter((metric) => metric.isString === true),
    ];
}

export function getCustomMetricNames(test) {
    const metrics = getCustomMetrics(test);

    const thresholdMetricNames = [];
    _.each(metrics, (metric) => {
        const thresholdMetrics = getThresholdsKeys(metric.name, test);
        if (thresholdMetrics.critical && thresholdMetrics.degraded) {
            thresholdMetricNames.push(thresholdMetrics.critical);
            thresholdMetricNames.push(thresholdMetrics.degraded);
        }
    });

    return [...CUSTOM_METRICS, ...metrics.map(modifyMetricName), ...thresholdMetricNames];
}

export function getTestMetrics(test) {
    const testType = test?.typeNode;
    switch (testType?.group) {
        case TEST_TYPE_GROUPS.WEB:
            return getWebMetricNames(test);
        case TEST_TYPE_GROUPS.VOIP:
            return getVoIPMetricNames(test);
        default:
            return getCustomMetricNames(test);
    }
}

export function getTestPathMetrics(test) {
    // Currently only web tests support path metrics.
    return getWebPathMetricNames(test);
}

export function getTestSearchFields(test) {
    const testType = test?.typeNode;
    switch (testType?.group) {
        case TEST_TYPE_GROUPS.WEB:
            return [...WEB_SEARCH_FIELDS, ...getWifiSearchFields(test)];
        case TEST_TYPE_GROUPS.VOIP:
            return [...BASE_SEARCH_FIELDS, ...getWifiSearchFields(test)];
        default:
            return [...BASE_SEARCH_FIELDS, ...getWifiSearchFields(test)];
    }
}

export function getTestPathSearchFields() {
    // Currently only web tests support path metrics, and only base fields are
    // searchable.
    return [...BASE_SEARCH_FIELDS];
}

export function buildThresholdColumns(degradedMapping, excessiveMapping, metricTitle, formatter) {
    function formatTitle(thresholdTitle) {
        const title = _.isObject(metricTitle) ? i18n._(metricTitle) : metricTitle;
        return `${title} ${i18n._(thresholdTitle)}`;
    }

    const csvOnly = true;

    const thresholdColumns = {
        degradedColumn: {},
        excessiveColumn: {},
    };

    if (!degradedMapping || !excessiveMapping) {
        return thresholdColumns;
    }

    thresholdColumns.degradedColumn = {
        csvOnly,
        type: COLUMN_TYPES.NUMERIC,
        title: formatTitle(t`Degraded Threshold `),
        mapping: degradedMapping,
        formatter,
    };
    thresholdColumns.excessiveColumn = {
        csvOnly,
        type: COLUMN_TYPES.NUMERIC,
        title: formatTitle(t`Excessive Threshold `),
        mapping: excessiveMapping,
        formatter,
    };

    return thresholdColumns;
}
